import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ToastContainer } from "@/components/ui/toast";
import { useUnifiedAuth } from "@/lib/auth/use-unified-auth";
import type { User } from '@/types';
import { Navigation } from "./components/Navigation";
import { Footer } from "./components/Footer";
import { NetworkErrorBanner } from "./components/ui/NetworkErrorBanner";
import { Home } from "./pages/Home";
import { Experiences } from "./pages/Experiences";
import { Creators } from "./pages/Creators";
import { CreatorProfile } from "./pages/creators/[id]";
import { About } from "./pages/About";
import { Apply } from "./pages/Apply";
import { Settings } from "./pages/profile/Settings";
import { Profile } from "./pages/profile/Profile";
import AdminPage from "./pages/admin";
import { CreatorDashboard } from "./pages/creator/Dashboard";
import { CreateExperience } from "./pages/creator/CreateExperience";
import { EditExperience } from "./pages/creator/EditExperience";
import { ExperiencesList } from "./pages/creator/ExperiencesList";
import { BookExperience } from "./pages/experiences/[id]/book";
import { ExperienceDetails } from "./pages/experiences/[id]";
import { AzoresExperience } from "./pages/experiences/azores";
import { Privacy } from "./pages/legal/Privacy";
import { Terms } from "./pages/legal/Terms";
import { Cookies } from "./pages/legal/Cookies";
import { AuthCallback } from "./pages/auth/Callback";
import { Azores } from "./pages/Azores";
import { Lucido as LucidoApril } from "./pages/Lucido-April-12";
// Import the static experience components
import { AyuniExperience } from "./pages/experiences/ayuni";
import { ArtHouseExperience } from "./pages/experiences/arthouse";
import { ArtHouseMay22 } from "./pages/experiences/arthouse-may-22";
import { AegeanExperience } from "./pages/experiences/aegean";
import { AegeanOdysseyVoyage2 } from "./pages/experiences/aegean2";
import { JoieDeVivreExperience } from "./pages/experiences/joiedevivre";
import { SunscapesExperience } from "./pages/experiences/sunscapes";
import { XamanicExperience } from "./pages/experiences/xamanic";
import { MetaTags } from '@/components/MetaTags';
import { supabase } from './lib/supabase/client';
// Removed deprecated mainStore import
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
// Session restore is now handled internally by unified auth service
import ClaimTicket from '@/pages/ClaimTicket';
import { Contact } from "./pages/Contact";
import Bookings from "./pages/Bookings";
import { logger } from '@/lib/utils/logger';

// Protected route guard component
function ProtectedRoute({
  children,
  requiredRole,
}: {
  children: React.ReactNode;
  requiredRole?: string | string[];
}) {
  const { user, profile, isLoading: loading } = useUnifiedAuth();
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  React.useEffect(() => {
    if (!loading) {
      setIsInitialLoad(false);
    }
  }, [loading]);

  // Show loading state only on initial load
  if (loading && isInitialLoad) {
    return <LoadingScreen />;
  }

  // Redirect if not authenticated
  if (!loading && !user) {
    return <Navigate to="/" replace />;
  }

  // Check role requirements
  if (user && requiredRole) {
    const allowedRoles = Array.isArray(requiredRole)
      ? requiredRole
      : [requiredRole];

    // Use profile role from unified auth
    const userRole = profile?.role || 'user';
    if (!allowedRoles.includes(userRole) && userRole !== "admin") {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}

function App() {
  // Clear any bad tokens immediately on app startup
  // This prevents the auth system from attempting to use invalid refresh tokens
  // which would cause 400 errors and retry loops. By detecting and clearing
  // malformed tokens early, we ensure a clean auth initialization.
  React.useEffect(() => {
    const checkAndClearBadTokens = async () => {
      try {
        // Check if we have tokens in localStorage
        const authKey = 'sb-pmtawanclqujznxnbhng-auth-token';
        const storedAuth = localStorage.getItem(authKey);
        
        if (storedAuth) {
          try {
            const parsed = JSON.parse(storedAuth);
            // If we have a refresh token but it's causing issues, test it
            if (parsed.refresh_token) {
              // Quick validation - if the token looks malformed or expired
              const tokenParts = parsed.refresh_token.split('.');
              if (tokenParts.length !== 3) {
                logger.warn('Malformed refresh token detected, clearing session');
                localStorage.removeItem(authKey);
                await supabase.auth.signOut();
                return;
              }
              
              // Check if access token is expired
              if (parsed.expires_at && parsed.expires_at * 1000 < Date.now()) {
                logger.debug('Access token expired, will attempt refresh');
              }
            }
          } catch (e) {
            logger.error('Failed to parse auth token, clearing:', e);
            localStorage.removeItem(authKey);
          }
        }
      } catch (error) {
        logger.error('Error checking tokens:', error);
      }
    };
    
    checkAndClearBadTokens();
  }, []);

  // Always call hooks at the top, before any return
  // Session restore is now handled internally by unified auth service
  const { user, isLoading: loading } = useUnifiedAuth();
  // Removed deprecated setUser from mainStore

  // Add periodic session validation to prevent stale tokens
  React.useEffect(() => {
    // Monitor localStorage directly to debug session loss - DEV only
    if (import.meta.env.DEV) {
      let lastLocalStorageCount = 0;
      const monitorStorage = () => {
        const allKeys = Object.keys(localStorage);
        const supabaseKeys = allKeys.filter(k => k.includes('supabase'));
        
        // Detect if localStorage was cleared or Supabase keys were removed
        if ((lastLocalStorageCount > 0 && allKeys.length === 0) || 
            (supabaseKeys.length === 0 && Object.keys(sessionStorage).some(k => k.includes('supabase')))) {
          logger.error('localStorage was cleared or Supabase keys removed! Attempting recovery...');
          
          // Use the storage backup system to recover
          import('./lib/supabase/storage-backup').then(async ({ storageBackup }) => {
            const authTokenKey = 'sb-pmtawanclqujznxnbhng-auth-token';
            const recoveredValue = await storageBackup.restore(authTokenKey);
            
            if (recoveredValue) {
              try {
                localStorage.setItem(authTokenKey, recoveredValue);
                logger.info('Recovered auth token from backup system');
              } catch (e) {
                logger.error('Failed to restore auth token:', e);
              }
            } else {
              logger.debug('No auth token found in backup system');
            }
          });
          
          // Force a re-check of auth state after recovery
          setTimeout(() => {
            logger.debug('Triggering auth state check after recovery...');
            supabase.auth.getSession();
          }, 500);
        }
        
        lastLocalStorageCount = allKeys.length;
        
        logger.debug(`localStorage: ${allKeys.length} total keys, ${supabaseKeys.length} Supabase keys`);
        supabaseKeys.forEach(key => {
          const value = localStorage.getItem(key);
          if (value && key.includes('auth-token')) {
            try {
              const parsed = JSON.parse(value);
              logger.debug(`${key} has keys:`, Object.keys(parsed).join(', '));
            } catch (e) {
              logger.warn(`${key} has invalid JSON`);
            }
          }
        });
      };

      // Add storage event listener for immediate detection
      const handleStorageChange = (e: StorageEvent) => {
        if (e.storageArea === localStorage && e.key && e.key.includes('supabase') && !e.newValue) {
          logger.warn('Supabase key removed from localStorage:', e.key);
          // Immediate recovery attempt using the backup system
          import('./lib/supabase/storage-backup').then(async ({ storageBackup }) => {
            const recoveredValue = await storageBackup.restore(e.key!);
            if (recoveredValue) {
              logger.info('Immediately restoring from backup system');
              try {
                localStorage.setItem(e.key!, recoveredValue);
              } catch (err) {
                logger.error('Failed to restore key:', err);
              }
            }
          });
        }
      };
      
      window.addEventListener('storage', handleStorageChange);
      
      // Initial check after a short delay to ensure auth is set up
      setTimeout(monitorStorage, 1000);
      
      // Monitor storage every 2 seconds for more frequent detection
      const monitorInterval = setInterval(monitorStorage, 2000);

      // Clean up on unmount
      return () => {
        clearInterval(monitorInterval);
        window.removeEventListener('storage', handleStorageChange);
      };
    }

    // IMPORTANT: This is needed to migrate users from old auth system
    // Users with old tokens get stuck on loading page without this
    const checkSession = async () => {
      const currentUser = user; // Use the hook value directly
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          logger.warn('Session check error:', error.message);
        }
        // If we still have a valid session or not logged-in, nothing to do
        if (session || !currentUser) return;

        // No session but UI thinks user is logged-in → try silent refresh
        const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshed.session) {
          logger.info('Session silently refreshed');
          return;
        }
        logger.warn('Session refresh failed:', refreshError?.message);
        // Don't force sign-out on refresh failure - let user stay logged in
        // The auth system will handle actual auth failures when making API calls
        logger.info('Session refresh failed but keeping user logged in');
        // Only log the issue, don't sign out
        // if (currentUser && refreshError) {
        //   console.log('🔄 Forcing re-authentication for user with stale token');
        //   await supabase.auth.signOut();
        // }
      } catch (err) {
        logger.error('Session validation unexpected error:', err);
      }
    };

    // Initial check after mount - increased delay to avoid race conditions
    const timeoutId = setTimeout(checkSession, 30000); // 30 seconds
    
    // Periodic checks every 5 minutes (much less aggressive)
    const intervalId = setInterval(checkSession, 300000); // 5 minutes

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [user]); // Add user dependency

  // User synchronization is now handled by the auth system
  // No need for manual store synchronization

  if (loading) {
    return <LoadingScreen />;
  }

  // Expose for debugging
  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    window.supabase = supabase;
    // Unified auth service debug utilities
    import('./lib/auth/unified-auth-service').then(({ unifiedAuthService }) => {
      window.unifiedAuthService = unifiedAuthService;
      window.resetAuth = async () => {
        logger.info('Manual auth reset triggered');
        await unifiedAuthService.signOut();
      };
    });
    
    // Import and expose new auth utilities
    import('./lib/auth/debug').then(({ debugAuthState, clearAuthData, getAuthTraces }) => {
      window.debugAuthState = debugAuthState;
      window.clearAuthData = clearAuthData;
      window.getAuthTraces = getAuthTraces;
      logger.debug('Debug utilities loaded');
    }).catch(err => logger.error('Failed to load debug utilities:', err));
    
    import('./lib/auth/monitoring').then(({ authMonitor }) => {
      window.authMonitor = authMonitor;
      logger.debug('Monitoring loaded');
    }).catch(err => logger.error('Failed to load monitoring:', err));
    
    import('./lib/auth/fix-loading').then(({ fixAuthLoading, resetAuthCompletely }) => {
      window.fixAuthLoading = fixAuthLoading;
      window.resetAuthCompletely = resetAuthCompletely;
      logger.debug('Fix utilities loaded');
    }).catch(err => logger.error('Failed to load fix utilities:', err));
    
    import('./lib/auth/status').then(({ authSystemStatus }) => {
      window.authSystemStatus = authSystemStatus;
      logger.debug('Status dashboard loaded');
    }).catch(err => logger.error('Failed to load status:', err));
  }

  return (
    <HelmetProvider>
      <MetaTags 
        title="rendezvous - Curated Experiences"
        description="Discover extraordinary moments through curated experiences that inspire, transform, and connect."
        image="https://rendezvouswebsite.b-cdn.net/DSCF9542.JPG"
        url="https://letsrendezvous.co"
      />

      <Router>
        <div className="min-h-screen bg-earth-900 text-sand-50">
          <NetworkErrorBanner />
          <Navigation />
          <ToastContainer />
          <main>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/apply" element={<Apply />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/experiences" element={<Experiences />} />
              <Route path="/creators" element={<Creators />} />
              <Route path="/creators/:id" element={<CreatorProfile />} />
              <Route path="/experiences/:id" element={<ExperienceDetails />} />
              <Route path="/experiences/:id/book" element={<BookExperience />} />
              <Route path="/experiences/arthouse" element={<ArtHouseExperience />} />
              <Route path="/experiences/arthouse-may-22" element={<ArtHouseMay22 />} />
              <Route path="/experiences/ayuni" element={<AyuniExperience />} />
              <Route path="/experiences/joiedevivre" element={<JoieDeVivreExperience />} />
              <Route path="/experiences/sunscapes" element={<SunscapesExperience />} />
              <Route path="/experiences/xamanic" element={<XamanicExperience />} />
              <Route path="/experiences/aegean" element={<AegeanExperience />} />
              <Route path="/experiences/aegean2" element={<AegeanOdysseyVoyage2 />} />
              <Route path="/experiences/azores" element={<AzoresExperience />} />
              <Route path="/legal/privacy" element={<Privacy />} />
              <Route path="/legal/terms" element={<Terms />} />
              <Route path="/legal/cookies" element={<Cookies />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/azores" element={<Azores />} />
              <Route path="/lucido-april-12" element={<LucidoApril />} />
              
              {/* Claim ticket route - public */}
              <Route path="/claim/ticket/:token" element={<ClaimTicket />} />

              {/* Protected Routes */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookings"
                element={
                  <ProtectedRoute>
                    <Bookings />
                  </ProtectedRoute>
                }
              />

              {/* Creator Routes */}
              <Route
                path="/creator/dashboard"
                element={
                  <ProtectedRoute requiredRole="creator">
                    <CreatorDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/creator/experiences/new"
                element={
                  <ProtectedRoute requiredRole={["creator", "admin"]}>
                    <CreateExperience />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/creator/experiences/:id/edit"
                element={
                  <ProtectedRoute requiredRole="creator">
                    <EditExperience />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/creator/experiences"
                element={
                  <ProtectedRoute requiredRole="creator">
                    <ExperiencesList />
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes */}
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminPage />
                  </ProtectedRoute>
                }
              />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;

declare global {
  interface Window {
    supabase: typeof supabase;
    unifiedAuthService: any;
    resetAuth: () => void;
    debugAuthState: any;
    clearAuthData: any;
    getAuthTraces: any;
    authMonitor: any;
    fixAuthLoading: any;
    resetAuthCompletely: any;
    authSystemStatus: any;
  }
}